import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { Row, Col, Table, Button, Form, InputGroup, Badge, Modal } from 'react-bootstrap';
import { useSnackbar } from 'notistack';
import { ProductContext } from '../../ProductProvider';
import { fetchAuthSession } from 'aws-amplify/auth';
import axios from 'axios';
import Preloader from '../../components/Preloader/Preloader';
import './Reports.css';

const config = require('../../config.json');

export default function JournalIssuesReport() {
  const { validateLogin, loggedUser } = useContext(ProductContext);
  const { enqueueSnackbar } = useSnackbar();

  // Date Range State (Default: Last 7 Days)
  const getTodayStr = () => new Date().toISOString().split('T')[0];
  const getPastDateStr = (daysAgo) => {
    const d = new Date();
    d.setDate(d.getDate() - daysAgo);
    return d.toISOString().split('T')[0];
  };

  const [startDate, setStartDate] = useState(getPastDateStr(7));
  const [endDate, setEndDate] = useState(getTodayStr());
  const [activePreset, setActivePreset] = useState('7days');

  // Loading & Data State
  const [loaded, setLoaded] = useState(false);
  const [issuesList, setIssuesList] = useState([]);
  const [statusFilter, setStatusFilter] = useState('Received'); // 'Received' by default
  const [selectedJournalFilter, setSelectedJournalFilter] = useState('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchTopic, setSearchTopic] = useState('JournalTitle');
  const [searchValue, setSearchValue] = useState('ALL');

  // Sorting
  const [sortField, setSortField] = useState('receivedDate');
  const [sortOrder, setSortOrder] = useState('desc');

  // Details Modal
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Apply Quick Date Presets
  const handlePresetSelect = (preset) => {
    setActivePreset(preset);
    const today = getTodayStr();
    let newStart = startDate;
    let newEnd = today;

    switch (preset) {
      case 'today':
        newStart = today;
        newEnd = today;
        break;
      case '7days':
        newStart = getPastDateStr(7);
        newEnd = today;
        break;
      case '14days':
        newStart = getPastDateStr(14);
        newEnd = today;
        break;
      case '30days':
        newStart = getPastDateStr(30);
        newEnd = today;
        break;
      case 'thisMonth': {
        const d = new Date();
        newStart = new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
        newEnd = today;
        break;
      }
      case '90days':
        newStart = getPastDateStr(90);
        newEnd = today;
        break;
      default:
        break;
    }

    setStartDate(newStart);
    setEndDate(newEnd);
    fetchJournalIssuesReport(newStart, newEnd);
  };

  // Fetch Journal Issues Report from Backend API
  const fetchJournalIssuesReport = async (sDate = startDate, eDate = endDate) => {
    try {
      setLoaded(false);
      const { idToken } = (await fetchAuthSession()).tokens ?? {};
      const clientID = loggedUser?.website || 'VJCollege';
      const searchValue = 'Received';
      const searchTopic = 'IssueReceived';

      // Backend API call as requested
      const response = await axios.get(`${config.api.invokeURL}/vjlibrary/search/journal/issues`, {
        params: {
          fetchKey: searchValue,
          fetchOption: searchTopic,
          clientID: clientID,
          startDate: sDate,
          endDate: eDate
        },
        headers: {
          Authorization: idToken,
          'x-api-key': config.api.key
        }
      });
      console.log("Response : ", response.data);
      const rawData = Array.isArray(response.data) ? response.data : [];
      setIssuesList(rawData);
      setLoaded(true);
      enqueueSnackbar(`Journal issues data loaded (${rawData.length} records)`, { variant: 'success' });
    } catch (error) {
      console.error('Error fetching journal issues report:', error);
      enqueueSnackbar('Error loading journal issues: ' + (error.response?.data?.message || error.message), { variant: 'warning' });
      setLoaded(true);
    }
  };

  useEffect(() => {
    if (!loggedUser?.isAuthenticated) {
      validateLogin();
    } else {
      fetchJournalIssuesReport(startDate, endDate);
    }
  }, [loggedUser]);

  // Extract distinct journals list for the dropdown filter
  const distinctJournals = useMemo(() => {
    const map = new Map();
    issuesList.forEach((item) => {
      const id = item.journalID;
      const title = item.journalTitle || item.title || `Journal ${id}`;
      if (id && !map.has(id)) {
        map.set(id, title);
      }
    });
    return Array.from(map.entries()).map(([id, title]) => ({ id, title }));
  }, [issuesList]);

  // Filter and process issues based on:
  // 1. Date range (receivedDate or updatedOn / createdOn falling in [startDate, endDate])
  // 2. Status filter
  // 3. Journal dropdown filter
  // 4. Search query
  const filteredIssues = useMemo(() => {
    return issuesList.filter((item) => {
      // 1. Date Range Matching
      const itemDateStr = item.receivedDate || (item.updatedOn ? item.updatedOn.split('T')[0] : '') || (item.createdOn ? item.createdOn.split('T')[0] : '');

      if (itemDateStr) {
        if (startDate && itemDateStr < startDate) return false;
        if (endDate && itemDateStr > endDate) return false;
      } else {
        // If item has no receivedDate and we are filtering strictly, check if pubDate or expDate falls in range
        const fallbackDate = item.pubDate || item.expDate;
        if (fallbackDate) {
          if (startDate && fallbackDate < startDate) return false;
          if (endDate && fallbackDate > endDate) return false;
        }
      }

      // 2. Status Filter
      if (statusFilter !== 'ALL') {
        if (statusFilter === 'Received' && item.issueStatus !== 'Received') return false;
        if (statusFilter === 'Expected' && item.issueStatus !== 'Expected' && item.issueStatus !== 'Pending') return false;
        if (statusFilter === 'Missing' && item.issueStatus !== 'Missing' && item.issueStatus !== 'Damaged') return false;
      }

      // 3. Journal Dropdown Filter
      if (selectedJournalFilter !== 'ALL' && item.journalID !== selectedJournalFilter) {
        return false;
      }

      // 4. Search term matching
      if (searchTerm.trim() !== '') {
        const term = searchTerm.toLowerCase();
        const title = (item.journalTitle || item.title || '').toLowerCase();
        const jId = (item.journalID || '').toLowerCase();
        const vol = (item.volNo ?? item.volume ?? '').toString().toLowerCase();
        const iss = (item.issueNumber ?? '').toString().toLowerCase();
        const year = (item.issueYear ?? item.year ?? '').toString().toLowerCase();
        const updatedBy = (item.updatedBy || '').toLowerCase();
        const issuedTo = (item.issuedTo || '').toLowerCase();
        const seq = (item.seq || '').toString().toLowerCase();

        const matches =
          title.includes(term) ||
          jId.includes(term) ||
          vol.includes(term) ||
          iss.includes(term) ||
          year.includes(term) ||
          updatedBy.includes(term) ||
          issuedTo.includes(term) ||
          seq.includes(term);

        if (!matches) return false;
      }

      return true;
    });
  }, [issuesList, startDate, endDate, statusFilter, selectedJournalFilter, searchTerm]);

  // Sorted list
  const sortedIssues = useMemo(() => {
    const list = [...filteredIssues];
    list.sort((a, b) => {
      let valA = a[sortField];
      let valB = b[sortField];

      if (sortField === 'receivedDate' || sortField === 'pubDate' || sortField === 'updatedOn') {
        const timeA = valA ? new Date(valA).getTime() : 0;
        const timeB = valB ? new Date(valB).getTime() : 0;
        return sortOrder === 'asc' ? timeA - timeB : timeB - timeA;
      }

      if (sortField === 'volNo' || sortField === 'issueNumber' || sortField === 'issueYear' || sortField === 'seq') {
        const numA = Number(valA) || 0;
        const numB = Number(valB) || 0;
        return sortOrder === 'asc' ? numA - numB : numB - numA;
      }

      const strA = (valA || '').toString().toLowerCase();
      const strB = (valB || '').toString().toLowerCase();
      if (strA < strB) return sortOrder === 'asc' ? -1 : 1;
      if (strA > strB) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [filteredIssues, sortField, sortOrder]);

  // Summary Metrics (KPIs)
  const kpiStats = useMemo(() => {
    const totalReceived = filteredIssues.filter((i) => i.issueStatus === 'Received').length;
    const journalsSet = new Set(filteredIssues.map((i) => i.journalID).filter(Boolean));
    const volumesSet = new Set(filteredIssues.map((i) => `${i.journalID}-${i.volNo || i.volume}`).filter(Boolean));
    const totalIssues = filteredIssues.length;

    return {
      totalReceived,
      totalIssues,
      distinctJournals: journalsSet.size,
      distinctVolumes: volumesSet.size
    };
  }, [filteredIssues]);

  // Sorting Handler
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const renderSortIcon = (field) => {
    if (sortField !== field) {
      return <i className="bi bi-arrow-down-up text-muted ms-1 small" style={{ opacity: 0.35 }}></i>;
    }
    return sortOrder === 'asc' ? (
      <i className="bi bi-arrow-up text-primary ms-1"></i>
    ) : (
      <i className="bi bi-arrow-down text-primary ms-1"></i>
    );
  };

  // Date Formatting Helper
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  // Export to CSV Functionality
  const exportToCSV = () => {
    if (sortedIssues.length === 0) {
      enqueueSnackbar('No records to export', { variant: 'info' });
      return;
    }

    const headers = [
      'S.No',
      'Received Date',
      'Journal ID',
      'Journal Title',
      'Volume No',
      'Issue Number',
      'Vol-Issue Code',
      'Issue Year',
      'Issue Month',
      'Publication Date',
      'Status',
      'Received / Updated By'
    ];

    const rows = sortedIssues.map((item, idx) => [
      idx + 1,
      item.receivedDate || (item.updatedOn ? item.updatedOn.split('T')[0] : '') || '',
      item.journalID || '',
      `"${(item.journalTitle || item.title || '').replace(/"/g, '""')}"`,
      item.volNo ?? item.volume ?? '',
      item.issueNumber ?? '',
      item.volissuesNo ?? '',
      item.issueYear ?? item.year ?? '',
      item.issueMonth ?? item.month ?? '',
      item.pubDate || '',
      item.issueStatus || 'Received',
      item.updatedBy || 'Librarian'
    ]);

    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map((e) => e.join(','))].join('\n');

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `journal_issues_received_report_${startDate}_to_${endDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    enqueueSnackbar('Journal Issues Received Report exported successfully!', { variant: 'success' });
  };

  // Print Report Handler
  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="reports-section">
      {!loaded && <Preloader show={true} />}

      {/* Printable Header */}
      <div className="print-only-header">
        <h2>Vidyajyoti College of Theology - Library</h2>
        <h4>Journal Issues Received Report</h4>
        <p>Period: {formatDate(startDate)} to {formatDate(endDate)} | Generated On: {formatDate(new Date())}</p>
        <hr />
      </div>

      {/* KPI Cards Grid */}
      <div className="reports-kpi-grid">
        <div className="reports-kpi-card">
          <div className="reports-kpi-icon bg-success bg-opacity-10 text-success">
            <i className="bi bi-box-seam"></i>
          </div>
          <div className="reports-kpi-info">
            <div className="reports-kpi-val text-success">{kpiStats.totalReceived}</div>
            <div className="reports-kpi-lbl">Issues Received</div>
            <div className="reports-kpi-sub">In selected date range</div>
          </div>
        </div>

        <div className="reports-kpi-card">
          <div className="reports-kpi-icon bg-primary bg-opacity-10 text-primary">
            <i className="bi bi-journal-text"></i>
          </div>
          <div className="reports-kpi-info">
            <div className="reports-kpi-val text-primary">{kpiStats.distinctJournals}</div>
            <div className="reports-kpi-lbl">Distinct Journals</div>
            <div className="reports-kpi-sub">Titles with arrivals</div>
          </div>
        </div>

        <div className="reports-kpi-card">
          <div className="reports-kpi-icon bg-info bg-opacity-10 text-info">
            <i className="bi bi-collection"></i>
          </div>
          <div className="reports-kpi-info">
            <div className="reports-kpi-val text-info">{kpiStats.distinctVolumes}</div>
            <div className="reports-kpi-lbl">Volumes Covered</div>
            <div className="reports-kpi-sub">Unique volume series</div>
          </div>
        </div>

        <div className="reports-kpi-card">
          <div className="reports-kpi-icon bg-warning bg-opacity-10 text-warning">
            <i className="bi bi-calendar-range"></i>
          </div>
          <div className="reports-kpi-info">
            <div className="reports-kpi-val fs-5 text-dark fw-bold">
              {formatDate(startDate)} - {formatDate(endDate)}
            </div>
            <div className="reports-kpi-lbl">Reporting Window</div>
            <div className="reports-kpi-sub">Customizable date filter</div>
          </div>
        </div>
      </div>

      {/* Filter & Date Selection Card */}
      <div className="reports-filter-card">
        <div className="reports-filter-title">
          <i className="bi bi-sliders text-primary"></i> Filter Issues by Received Date & Criteria
        </div>

        {/* Quick Date Presets */}
        <div className="reports-date-pill-group">
          <span className="small text-muted align-self-center me-1 fw-bold">Presets:</span>
          <button
            type="button"
            className={`reports-date-pill ${activePreset === 'today' ? 'active' : ''}`}
            onClick={() => handlePresetSelect('today')}
          >
            Today
          </button>
          <button
            type="button"
            className={`reports-date-pill ${activePreset === '7days' ? 'active' : ''}`}
            onClick={() => handlePresetSelect('7days')}
          >
            Last 7 Days (Default)
          </button>
          <button
            type="button"
            className={`reports-date-pill ${activePreset === '14days' ? 'active' : ''}`}
            onClick={() => handlePresetSelect('14days')}
          >
            Last 14 Days
          </button>
          <button
            type="button"
            className={`reports-date-pill ${activePreset === '30days' ? 'active' : ''}`}
            onClick={() => handlePresetSelect('30days')}
          >
            Last 30 Days
          </button>
          <button
            type="button"
            className={`reports-date-pill ${activePreset === 'thisMonth' ? 'active' : ''}`}
            onClick={() => handlePresetSelect('thisMonth')}
          >
            This Month
          </button>
          <button
            type="button"
            className={`reports-date-pill ${activePreset === '90days' ? 'active' : ''}`}
            onClick={() => handlePresetSelect('90days')}
          >
            Last 3 Months
          </button>
        </div>

        {/* Date Inputs & Dropdown Filters Row */}
        <Row className="g-3 align-items-end">
          <Col lg={3} md={6} sm={6}>
            <Form.Group>
              <Form.Label className="small fw-bold text-secondary mb-1">
                <i className="bi bi-calendar-check me-1"></i> Start Date
              </Form.Label>
              <Form.Control
                type="date"
                size="sm"
                className="reports-input"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setActivePreset('custom');
                }}
              />
            </Form.Group>
          </Col>

          <Col lg={3} md={6} sm={6}>
            <Form.Group>
              <Form.Label className="small fw-bold text-secondary mb-1">
                <i className="bi bi-calendar-event me-1"></i> End Date
              </Form.Label>
              <Form.Control
                type="date"
                size="sm"
                className="reports-input"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setActivePreset('custom');
                }}
              />
            </Form.Group>
          </Col>

          <Col lg={3} md={6} sm={6}>
            <Form.Group>
              <Form.Label className="small fw-bold text-secondary mb-1">
                <i className="bi bi-journal-bookmark me-1"></i> Journal Filter
              </Form.Label>
              <Form.Select
                size="sm"
                className="reports-input"
                value={selectedJournalFilter}
                onChange={(e) => setSelectedJournalFilter(e.target.value)}
              >
                <option value="ALL">All Journals ({distinctJournals.length})</option>
                {distinctJournals.map((j) => (
                  <option key={j.id} value={j.id}>
                    {j.title} ({j.id})
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>

          <Col lg={3} md={6} sm={6}>
            <Form.Group>
              <Form.Label className="small fw-bold text-secondary mb-1">
                <i className="bi bi-check2-circle me-1"></i> Issue Status
              </Form.Label>
              <Form.Select
                size="sm"
                className="reports-input"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="Received">Received Only</option>
                <option value="ALL">All Statuses</option>
                <option value="Expected">Expected / Pending</option>
                <option value="Missing">Missing / Damaged</option>
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        {/* Search Query & Action Buttons Row */}
        <Row className="g-2 align-items-center mt-2 pt-2 border-top">
          <Col lg={6} md={12}>
            <InputGroup size="sm">
              <InputGroup.Text className="bg-light border-end-0">
                <i className="bi bi-search text-muted"></i>
              </InputGroup.Text>
              <Form.Control
                type="text"
                className="reports-input border-start-0"
                placeholder="Search by title, journal ID, volume, issue #, updated by..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <Button variant="outline-secondary" onClick={() => setSearchTerm('')}>
                  <i className="bi bi-x"></i>
                </Button>
              )}
            </InputGroup>
          </Col>

          <Col lg={6} md={12} className="d-flex justify-content-lg-end gap-2 flex-wrap">
            <Button
              variant="primary"
              size="sm"
              className="fw-bold d-inline-flex align-items-center gap-1"
              onClick={() => fetchJournalIssuesReport(startDate, endDate)}
              title="Fetch journal issues from backend for the selected date range"
            >
              <i className="bi bi-arrow-clockwise"></i> Fetch Report
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              className="fw-bold d-inline-flex align-items-center gap-1"
              onClick={handlePrint}
              title="Print received issues report"
            >
              <i className="bi bi-printer"></i> Print
            </Button>
            <Button
              variant="success"
              size="sm"
              className="fw-bold d-inline-flex align-items-center gap-1"
              onClick={exportToCSV}
              disabled={sortedIssues.length === 0}
              title="Download as CSV spreadsheet"
            >
              <i className="bi bi-file-earmark-spreadsheet"></i> Export CSV
            </Button>
          </Col>
        </Row>
      </div>

      {/* Main Data Table Card */}
      <div className="reports-table-card">
        <div className="reports-table-header">
          <div className="d-flex align-items-center gap-2">
            <span className="fw-bold text-dark small">
              Found <strong>{sortedIssues.length}</strong> journal issues matching criteria
            </span>
            {activePreset !== 'custom' && (
              <Badge bg="primary" className="fw-normal">
                {activePreset === '7days' ? 'Last 7 Days' : activePreset}
              </Badge>
            )}
          </div>
          <div className="small text-muted">
            Click table columns to sort (Date, Title, Volume, Issue)
          </div>
        </div>

        {sortedIssues.length === 0 ? (
          <div className="reports-empty-state">
            <i className="bi bi-inbox reports-empty-icon"></i>
            <h6 className="fw-bold">No Journal Issues Found for Selected Date Range</h6>
            <p className="small text-muted mb-3">
              No issues received between <strong>{formatDate(startDate)}</strong> and <strong>{formatDate(endDate)}</strong>.
            </p>
            <Button variant="outline-primary" size="sm" onClick={() => handlePresetSelect('30days')}>
              Expand to Last 30 Days
            </Button>
          </div>
        ) : (
          <div className="table-responsive">
            <Table hover responsive className="reports-table mb-0 align-middle">
              <thead>
                <tr>
                  <th style={{ width: '45px' }} className="text-center">#</th>
                  <th style={{ minWidth: '120px', cursor: 'pointer' }} onClick={() => handleSort('receivedDate')}>
                    Received Date {renderSortIcon('receivedDate')}
                  </th>
                  <th style={{ minWidth: '240px', cursor: 'pointer' }} onClick={() => handleSort('journalTitle')}>
                    Journal Title & ID {renderSortIcon('journalTitle')}
                  </th>
                  <th style={{ minWidth: '130px', cursor: 'pointer' }} onClick={() => handleSort('volNo')}>
                    Vol & Issue # {renderSortIcon('volNo')}
                  </th>
                  <th style={{ minWidth: '120px', cursor: 'pointer' }} onClick={() => handleSort('issueYear')}>
                    Period / Year {renderSortIcon('issueYear')}
                  </th>
                  <th style={{ minWidth: '120px', cursor: 'pointer' }} onClick={() => handleSort('pubDate')}>
                    Pub Date {renderSortIcon('pubDate')}
                  </th>
                  <th style={{ minWidth: '110px' }}>Status</th>
                  <th style={{ minWidth: '120px', cursor: 'pointer' }} onClick={() => handleSort('updatedBy')}>
                    Received By {renderSortIcon('updatedBy')}
                  </th>
                  <th style={{ width: '80px' }} className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {sortedIssues.map((item, idx) => {
                  const isReceived = item.issueStatus === 'Received';
                  const dateDisplay = item.receivedDate || (item.updatedOn ? item.updatedOn.split('T')[0] : '');

                  return (
                    <tr key={`issue-row-${item.journalID}-${item.volNo}-${item.issueNumber}-${idx}`}>
                      <td className="text-center text-muted small">{idx + 1}</td>
                      <td>
                        <div className="fw-bold text-dark">{formatDate(dateDisplay)}</div>
                        {item.updatedOn && (
                          <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                            {new Date(item.updatedOn).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        )}
                      </td>
                      <td>
                        <div className="fw-bold text-dark">{item.journalTitle || item.title || 'Untitled Journal'}</div>
                        <span className="reports-badge-id">
                          {item.journalID || 'N/A'}
                        </span>
                      </td>
                      <td>
                        <div className="fw-semibold text-primary">
                          Vol {item.volNo ?? item.volume ?? '-'}, Iss #{item.issueNumber ?? '-'}
                        </div>
                        {item.volissuesNo && (
                          <span className="text-muted small font-monospace">
                            Key: {item.volissuesNo}
                          </span>
                        )}
                      </td>
                      <td>
                        <span className="badge bg-light text-dark border">
                          {item.issueMonth ? `M${item.issueMonth} / ` : ''}{item.issueYear || item.year || '-'}
                        </span>
                      </td>
                      <td className="text-muted small">
                        {formatDate(item.pubDate)}
                      </td>
                      <td>
                        {isReceived ? (
                          <span className="reports-badge-received">
                            <i className="bi bi-check-circle-fill"></i> Received
                          </span>
                        ) : item.issueStatus === 'Expected' || item.issueStatus === 'Pending' ? (
                          <span className="reports-badge-expected">
                            <i className="bi bi-clock-history"></i> Expected
                          </span>
                        ) : (
                          <span className="reports-badge-missing">
                            <i className="bi bi-exclamation-octagon"></i> {item.issueStatus || 'Missing'}
                          </span>
                        )}
                      </td>
                      <td>
                        <span className="small text-muted">{item.updatedBy || 'Librarian'}</span>
                      </td>
                      <td className="text-center">
                        <Button
                          variant="outline-info"
                          size="sm"
                          className="py-0 px-2"
                          title="View Full Issue Details"
                          onClick={() => {
                            setSelectedIssue(item);
                            setShowDetailModal(true);
                          }}
                        >
                          <i className="bi bi-eye"></i>
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-light fw-bold">
                  <td colSpan={6} className="text-end text-dark">
                    Total Issues Received in Selected Range:
                  </td>
                  <td colSpan={3} className="text-success fs-6">
                    {kpiStats.totalReceived} of {kpiStats.totalIssues} Issues
                  </td>
                </tr>
              </tfoot>
            </Table>
          </div>
        )}
      </div>

      {/* Full Issue Details Modal */}
      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} centered size="lg">
        <Modal.Header closeButton className="bg-light">
          <Modal.Title className="fs-6 fw-bold">
            <i className="bi bi-journal-check text-primary me-2"></i> Journal Issue Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedIssue && (
            <Row className="g-3">
              <Col md={12}>
                <div className="p-3 bg-light rounded border">
                  <h6 className="fw-bold text-primary mb-1">
                    {selectedIssue.journalTitle || selectedIssue.title || 'Untitled Journal'}
                  </h6>
                  <div className="small text-muted font-monospace">
                    Journal ID: {selectedIssue.journalID} | Vol: {selectedIssue.volNo ?? selectedIssue.volume} | Issue: {selectedIssue.issueNumber}
                  </div>
                </div>
              </Col>
              <Col md={6}>
                <div className="p-2 border rounded">
                  <div className="text-muted small">Volume & Issue #</div>
                  <div className="fw-bold">Vol {selectedIssue.volNo ?? selectedIssue.volume}, Issue #{selectedIssue.issueNumber}</div>
                </div>
              </Col>
              <Col md={6}>
                <div className="p-2 border rounded">
                  <div className="text-muted small">Status</div>
                  <div className="fw-bold text-success">{selectedIssue.issueStatus || 'Received'}</div>
                </div>
              </Col>
              <Col md={6}>
                <div className="p-2 border rounded">
                  <div className="text-muted small">Received Date</div>
                  <div className="fw-bold">{formatDate(selectedIssue.receivedDate || selectedIssue.updatedOn)}</div>
                </div>
              </Col>
              <Col md={6}>
                <div className="p-2 border rounded">
                  <div className="text-muted small">Publication Date</div>
                  <div className="fw-bold">{formatDate(selectedIssue.pubDate)}</div>
                </div>
              </Col>
              <Col md={6}>
                <div className="p-2 border rounded">
                  <div className="text-muted small">Issue Year & Month</div>
                  <div className="fw-bold">{selectedIssue.issueYear || selectedIssue.year} (Month: {selectedIssue.issueMonth || selectedIssue.month || '-'})</div>
                </div>
              </Col>
              <Col md={6}>
                <div className="p-2 border rounded">
                  <div className="text-muted small">Sequence #</div>
                  <div className="fw-bold">{selectedIssue.seq || 'N/A'}</div>
                </div>
              </Col>
              <Col md={6}>
                <div className="p-2 border rounded">
                  <div className="text-muted small">Updated / Received By</div>
                  <div className="fw-bold">{selectedIssue.updatedBy || 'Librarian'}</div>
                </div>
              </Col>
              <Col md={6}>
                <div className="p-2 border rounded">
                  <div className="text-muted small">System Updated Timestamp</div>
                  <div className="fw-bold small font-monospace">{selectedIssue.updatedOn || 'N/A'}</div>
                </div>
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowDetailModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
